import ColorTag from 'components/misc/ColorTag'
import PhatDropdown from 'components/formElements/PhatDropdown'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'redux/hooks'
import { CardType, OWNED } from 'types/deck'
import Checkbox from '../Checkbox'
import CheckboxDropdown from '../CheckboxDropdown'

import styles from './selectMultipleCards.module.scss'

type Selection = 'all' | 'tag' | 'categories' | 'collection'
type CollectionStatus = 'exact' | 'altEdition' | 'none'

type Props = {
  onChange: (cards: CardType[]) => void
  includeOutOfDeckAsDefault?: boolean
  className?: string
}

const SelectMultipleCards = ({ onChange, includeOutOfDeckAsDefault = false, className }: Props) => {
  const [selectionOption, setSelectionOption] = useState<Selection>('all')
  const [includeOutOfDeck, setIncludeOutOfDeck] = useState(includeOutOfDeckAsDefault)
  const [selectedColorTags, setSelectedColorTags] = useState(new Array<string>())
  const [selectedCategories, setSelectedCategories] = useState(new Array<string>())
  const [selectedCollectionStatus, setSelectedCollectionStatus] = useState(new Array<CollectionStatus>())

  const tags = useAppSelector(state => state.deck.colorLabels)
  const categories = useAppSelector(state => state.deck.categories)

  const cardMap = useAppSelector(state => state.deck.cardMap)

  const cards = useMemo(() => {
    let cards = Object.values(cardMap)

    if (!includeOutOfDeck) cards = cards.filter(card => categories[card.categories[0]]?.includedInDeck)
    if (selectionOption === 'all') {
      onChange(cards)

      return cards
    }

    if (selectionOption === 'tag') cards = cards.filter(card => selectedColorTags.includes(card.colorLabel.name))
    if (selectionOption === 'categories') cards = cards.filter(card => selectedCategories.includes(card.categories[0]))
    if (selectionOption === 'collection') {
      cards = cards.filter(card => {
        if (selectedCollectionStatus.includes('exact') && card.owned === OWNED.YES) return true
        if (selectedCollectionStatus.includes('altEdition') && card.owned === OWNED.PRINTING) return true
        if (selectedCollectionStatus.includes('none') && card.owned === OWNED.NO) return true

        return false
      })
    }

    onChange(cards)

    return cards
  }, [cardMap, selectionOption, selectedColorTags, selectedCategories, selectedCollectionStatus, includeOutOfDeck])

  useEffect(() => {
    setIncludeOutOfDeck(includeOutOfDeckAsDefault)
  }, [includeOutOfDeckAsDefault])

  const categoryArray = Object.keys(categories)
    .map(key => categories[key])
    .sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className={className}>
      <PhatDropdown
        header="Select cards by"
        inlineHeader
        value={selectionOption}
        triggerWrapperClassName={styles.dropdown}
        options={[
          { label: 'All cards', onClick: () => setSelectionOption('all'), id: 'all' },
          { label: 'Color tags', onClick: () => setSelectionOption('tag'), id: 'tag' },
          { label: 'Categories', onClick: () => setSelectionOption('categories'), id: 'categories' },
          { label: 'Collection status', onClick: () => setSelectionOption('collection'), id: 'collection' },
        ]}
      />

      {selectionOption === 'tag' && (
        <CheckboxDropdown
          header="Color tags"
          options={tags.map(tag => ({
            id: `${tag.name}`,
            label: <ColorTag color={tag.color} name={tag.name} />,
          }))}
          onChange={setSelectedColorTags}
          placeholder="Choose color tags..."
          className={styles.dropdown}
          menuClassName={styles.menu}
          value={selectedColorTags}
          includeSelectAllNoneOptions
        />
      )}

      {selectionOption === 'categories' && (
        <CheckboxDropdown
          header="Categories"
          options={categoryArray.map(category => ({
            id: category.name,
            label: category.name,
          }))}
          onChange={setSelectedCategories}
          placeholder="Choose collection status..."
          className={styles.dropdown}
          menuClassName={styles.menu}
          value={selectedCategories}
          includeSelectAllNoneOptions
        />
      )}

      {selectionOption === 'collection' && (
        <CheckboxDropdown
          header="Collection status"
          options={[
            {
              label: 'Exact',
              id: 'exact',
            },
            {
              label: 'Other edition',
              id: 'altEdition',
            },
            {
              label: 'Not owned',
              id: 'none',
            },
          ]}
          onChange={setSelectedCollectionStatus}
          placeholder="Choose collection status..."
          className={styles.dropdown}
          value={selectedCollectionStatus}
          includeSelectAllNoneOptions
        />
      )}

      <Checkbox
        checked={includeOutOfDeck}
        label="Include out of deck cards (eg: maybeboard)"
        onChange={() => setIncludeOutOfDeck(!includeOutOfDeck)}
      />

      <div>
        Selected cards: <b>{cards.reduce((acc, card) => card.qty + acc, 0)}</b> (<b>{cards.length}</b> unique cards)
      </div>
    </div>
  )
}

export default SelectMultipleCards
